import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/safeClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

/**
 * Check if error is a stale/invalid session error that requires cleanup
 */
function isStaleSessionError(error: AuthError | Error | unknown): boolean {
  if (!error) return false;
  const message = (error as any)?.message?.toLowerCase() || '';
  const code = (error as any)?.code?.toLowerCase() || '';
  
  return (
    message.includes('refresh_token_not_found') ||
    message.includes('invalid refresh token') ||
    message.includes('session not found') ||
    code === 'refresh_token_not_found' ||
    code === 'session_not_found'
  );
}

// Session initialization timeout (10 seconds)
const SESSION_INIT_TIMEOUT = 10000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isMounted = true;

    // Safety timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('[AuthContext] Session initialization timeout - forcing completion');
        setLoading(false);
      }
    }, SESSION_INIT_TIMEOUT);

    // Check for existing session first with proper error handling
    const initSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          console.error('[AuthContext] getSession error:', error);
          
          // Handle stale session errors - clear and reset
          if (isStaleSessionError(error)) {
            console.warn('[AuthContext] Stale session detected, clearing...');
            try {
              await supabase.auth.signOut();
            } catch (signOutErr) {
              console.warn('[AuthContext] signOut during cleanup failed:', signOutErr);
            }
            setSession(null);
            setUser(null);
            setLoading(false);
            return;
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Update last_seen_at for online status
        if (session?.user) {
          (supabase.rpc as any)('update_last_seen').then(() => {}, () => {});
        }
      } catch (err) {
        console.error('[AuthContext] Critical error getting session:', err);
        
        if (!isMounted) return;
        
        // Handle stale session errors in catch block too
        if (isStaleSessionError(err)) {
          console.warn('[AuthContext] Stale session in catch, clearing...');
          try {
            await supabase.auth.signOut();
          } catch (signOutErr) {
            // Ignore
          }
        }
        
        // Ensure we still set null values so app can render
        setSession(null);
        setUser(null);
      } finally {
        // Always set loading to false to prevent infinite loading
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    // Set up auth state listener for future changes
    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        
        if (!isMounted) return;
        
        // Handle token refresh failures
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('[AuthContext] Token refresh failed, clearing session');
          setSession(null);
          setUser(null);
          return;
        }
        
        // Handle sign out events
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
      });
      subscription = data.subscription;
    } catch (err) {
      console.error('[AuthContext] Error setting up auth listener:', err);
    }

    // Periodic heartbeat for online status (every 2 minutes)
    const heartbeatInterval = setInterval(() => {
      if (isMounted) {
        supabase.auth.getSession().then(({ data }) => {
          if (data.session) {
            (supabase.rpc as any)('update_last_seen').then(() => {}, () => {});
          }
        });
      }
    }, 2 * 60 * 1000);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (subscription) {
        subscription.unsubscribe();
      }
      clearInterval(heartbeatInterval);
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('[AuthContext] signOut error:', err);
    }
    // Always clear local state even if signOut fails
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
